// Shared derivations over a BacktestResult (camelCase as the API serializes it). Reused by the results
// cards, the charts (U6), and the view shell (U7) so the no-data guard and series math live in one place.

// The persisted result can be a falsely-"successful" run when there's no/sparse market data: the backend
// hardcodes unfilledWindows=0 today (R-3c), so a run over a symbol/window with no data still reports
// status='done', isComplete=true, but an empty/flat equity curve. We derive a completeness signal we CAN
// trust from the equity curve itself (KTD7 / U5 no-data guard).
export function isResultMeaningful(result) {
  const curve = result?.equityCurve;
  return Array.isArray(curve) && curve.length >= 2;
}

// Per-period simple returns derived client-side from the equity curve (KTD4): r[i] = value[i]/value[i-1]-1.
// Live and historical results both derive here so the chart code path is identical.
export function deriveReturns(equityCurve) {
  if (!Array.isArray(equityCurve) || equityCurve.length < 2) return [];
  const out = [];
  for (let i = 1; i < equityCurve.length; i++) {
    const prev = equityCurve[i - 1].value;
    const cur = equityCurve[i].value;
    out.push({ time: equityCurve[i].time, value: prev ? cur / prev - 1 : 0 });
  }
  return out;
}

// Drawdown from a running peak (KTD4): dd[i] = value[i]/peak - 1 (<= 0).
export function deriveDrawdown(equityCurve) {
  if (!Array.isArray(equityCurve) || equityCurve.length < 1) return [];
  let peak = -Infinity;
  return equityCurve.map((p) => {
    peak = Math.max(peak, p.value);
    // Guard on peak > 0 (not truthiness) — a 0/negative peak (degenerate equity) yields no drawdown.
    return { time: p.time, value: peak > 0 ? p.value / peak - 1 : 0 };
  });
}

// Format a FRACTION (e.g. 0.123 → "+12.30%") as a signed percent string with tabular-friendly decimals.
// Use for client-derived series (deriveReturns/deriveDrawdown), which are fractions.
export function formatSignedPercent(fraction, decimals = 2) {
  return formatSignedPercentValue(fraction === null || fraction === undefined ? fraction : fraction * 100, decimals);
}

// Format a value that is ALREADY a percent (e.g. 12.3 → "+12.30%"). The backend's NetReturns,
// CumulativeReturns[].Return, and MaxDrawdown come out of the legacy-verbatim formulas pre-multiplied by
// 100 (PerformanceCalculator.ComputeCumulativeReturns / Statistics.MaxDrawdownPercent), so they must NOT be
// multiplied again — that was the +2462.74% / -2814.00% bug.
export function formatSignedPercentValue(pctValue, decimals = 2) {
  if (pctValue === null || pctValue === undefined || Number.isNaN(pctValue)) return '—';
  let pct = pctValue;
  // Normalize values that round to zero so a tiny negative doesn't render a misleading "-0.00%".
  if (Math.abs(pct) < 0.5 / 10 ** decimals) pct = 0;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(decimals)}%`;
}
